import React from 'react';
import {
  fetchLatestAppBuildIdentity,
  getCurrentAppBuildIdentity,
  isDifferentAppBuild,
  type AppBuildIdentity,
} from '../lib/appVersion';
import { reloadAppAfterAutosave } from '../lib/appReload';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const MIN_FOCUS_CHECK_MS = 60 * 1000;
const INITIAL_CHECK_DELAY_MS = 30 * 1000;
const COLLAPSED_KEY = 'site-survey:app-update-dismissed:v1';

function readCollapsedKey(): string {
  if (typeof window === 'undefined') return '';
  try {
    return window.sessionStorage.getItem(COLLAPSED_KEY) || '';
  } catch {
    return '';
  }
}

function writeCollapsedKey(value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(COLLAPSED_KEY, value);
  } catch {
    // Storage can be unavailable in hardened browser modes.
  }
}

export default function AppUpdateBanner() {
  const titleId = React.useId();
  const descriptionId = React.useId();
  const currentBuild = React.useMemo(() => getCurrentAppBuildIdentity(), []);
  const [latestBuild, setLatestBuild] = React.useState<AppBuildIdentity | null>(null);
  const [collapsedKey, setCollapsedKey] = React.useState(() => readCollapsedKey());
  const [reloadPending, setReloadPending] = React.useState(false);
  const [reloadError, setReloadError] = React.useState('');
  const lastCheckRef = React.useRef(0);
  const checkingRef = React.useRef(false);

  const checkForUpdate = React.useCallback(async (force = false) => {
    if (!currentBuild.releaseKey || checkingRef.current) return;
    if (latestBuild) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden' && !force) return;
    const now = Date.now();
    if (!force && now - lastCheckRef.current < MIN_FOCUS_CHECK_MS) return;
    lastCheckRef.current = now;
    checkingRef.current = true;
    try {
      const remote = await fetchLatestAppBuildIdentity();
      if (remote && isDifferentAppBuild(remote, currentBuild)) {
        setLatestBuild(remote);
      }
    } catch (err) {
      console.warn('[app-version] update check failed', err);
    } finally {
      checkingRef.current = false;
    }
  }, [currentBuild, latestBuild]);

  React.useEffect(() => {
    if (!currentBuild.releaseKey) return undefined;
    const initialTimer = window.setTimeout(() => {
      void checkForUpdate(true);
    }, INITIAL_CHECK_DELAY_MS);
    const interval = window.setInterval(() => {
      void checkForUpdate(false);
    }, CHECK_INTERVAL_MS);
    const handleFocus = () => {
      void checkForUpdate(false);
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') void checkForUpdate(false);
    };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.clearTimeout(initialTimer);
      window.clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [checkForUpdate, currentBuild.releaseKey]);

  if (!latestBuild) return null;

  const collapsed = latestBuild.releaseKey === collapsedKey;

  const handleReload = async () => {
    if (reloadPending) return;
    setReloadPending(true);
    setReloadError('');
    try {
      await reloadAppAfterAutosave();
    } catch (error) {
      console.error('[app-version] unable to save work before reload', error);
      setReloadError('We could not save all open work. Reload was cancelled. Fix the save error or try again.');
      setReloadPending(false);
    }
  };
  const handleCollapse = () => {
    writeCollapsedKey(latestBuild.releaseKey);
    setCollapsedKey(latestBuild.releaseKey);
  };
  const rootClassName = collapsed
    ? 'app-update-banner app-update-banner--compact'
    : 'app-update-banner app-update-banner--prominent';
  const description = collapsed
    ? 'Reload when you are ready to use the latest version.'
    : 'A new version has been deployed. Reload now for the latest app, or continue editing and reload after your current work is saved.';

  return (
    <div
      className={rootClassName}
      role={collapsed ? 'status' : 'dialog'}
      aria-live="polite"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      <div className="app-update-banner__panel">
        {!collapsed ? (
          <div className="app-update-banner__eyebrow">New build deployed</div>
        ) : null}
        <div className="app-update-banner__copy">
          <strong id={titleId}>Update available</strong>
          <span id={descriptionId}>{description}</span>
          {reloadError ? <span className="error" role="alert">{reloadError}</span> : null}
        </div>
        <div className="app-update-banner__actions">
          <button type="button" className="btn xs primary" onClick={() => void handleReload()} disabled={reloadPending}>
            {reloadPending ? 'Saving...' : collapsed ? 'Reload' : 'Reload now'}
          </button>
          {!collapsed ? (
            <button type="button" className="btn xs subtle" onClick={handleCollapse}>Continue editing</button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
